import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Link,
  Snackbar,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  Fade,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CookieIcon from "@mui/icons-material/Cookie";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Add TypeScript declaration for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface CookieConsentBannerProps {
  privacyPolicyUrl?: string;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  privacyPolicyUrl = "/privacy-policy",
}) => {
  // Three possible states: undefined (not decided), true (accepted), false (declined)
  const [cookieConsent, setCookieConsent] = useState<boolean | undefined>(
    () => {
      const savedConsent = localStorage.getItem("cookieConsent");
      if (savedConsent === "true") return true;
      if (savedConsent === "false") return false;
      return undefined;
    }
  );

  // Show the banner only if consent is undefined
  const [showBanner, setShowBanner] = useState(false);

  // Show a notification when user declines
  const [showDeclineNotice, setShowDeclineNotice] = useState(false);

  useEffect(() => {
    // Show banner after a short delay if consent hasn't been given yet
    if (cookieConsent === undefined) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowBanner(false);
    }
  }, [cookieConsent]);

  useEffect(() => {
    // Initialize analytics if consent is given
    if (cookieConsent === true) {
      initializeAnalytics();
    }
  }, [cookieConsent]);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "true");
    setCookieConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "false");
    setCookieConsent(false);
    setShowBanner(false);
    setShowDeclineNotice(true);
  };

  const initializeAnalytics = () => {
    // Initialize Google Analytics
    if (typeof window.gtag === "function") return; // Prevent re-initialization

    // @ts-ignore - gtag is defined in the script loaded conditionally
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    function gtag() {
      window.dataLayer.push(arguments);
    }
    // @ts-ignore
    gtag("js", new Date());
    // @ts-ignore
    gtag("config", "G-J6C5MWEJM2");

    // Dynamically load the Google Analytics script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-J6C5MWEJM2";
    document.head.appendChild(script);
  };

  // If user has already made a choice, don't render the banner
  if (!showBanner) {
    return (
      <Snackbar
        open={showDeclineNotice}
        autoHideDuration={6000}
        onClose={() => setShowDeclineNotice(false)}
        message="Analytics disabled. You can change this in your settings at any time."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    );
  }

  return (
    <>
      <Dialog
        open={showBanner}
        TransitionComponent={Fade}
        keepMounted
        aria-describedby="cookie-consent-dialog"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "white",
            py: 2,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CookieIcon />
          <Typography variant="h6" component="h2">
            Cookie Preferences
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography variant="body1" paragraph>
            We use cookies to enhance your browsing experience, personalize
            content, and analyze site traffic.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              my: 2,
              p: 2,
              bgcolor: "success.light",
              color: "success.contrastText",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleIcon fontSize="small" /> Benefits of accepting
              cookies:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Improved site performance and faster loading times</li>
              <li>Personalized experience based on your preferences</li>
              <li>
                Help us understand how you use our site so we can improve it
              </li>
            </Typography>
          </Box>

          <Typography variant="body2">
            By clicking "Accept All", you agree to the storing of cookies on
            your device. See our{" "}
            <Link
              component={RouterLink}
              to={privacyPolicyUrl}
              color="primary"
              underline="hover"
            >
              Privacy Policy
            </Link>{" "}
            for more details.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
          <Button
            variant="text"
            color="inherit"
            onClick={handleDecline}
            sx={{
              color: "text.secondary",
              fontSize: "0.875rem",
            }}
          >
            Decline
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAccept}
            startIcon={<CheckCircleIcon />}
            sx={{
              px: 3,
              fontWeight: "bold",
              boxShadow: 2,
            }}
          >
            Accept All
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showDeclineNotice}
        autoHideDuration={6000}
        onClose={() => setShowDeclineNotice(false)}
        message="Analytics disabled. You can change this in your settings at any time."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default CookieConsentBanner;
